/**
 * Saving as the person types, for the details editors (community and
 * enterprise): each target (the study, one image) is saved a short while after
 * its last change, or at once when asked (a field losing focus, Done), and one
 * save at a time per target, so a slower earlier answer can never land after a
 * later one. What a save sends is the editor's business: `save` reads the
 * target's state when it runs, so a burst of changes is one request.
 */
export interface Autosave {
  /** A change: saved once typing settles. */
  schedule: (key: string) => void;
  /** Save now: one target, or every target with a change waiting. Resolves when those saves have run. */
  flush: (key?: string) => Promise<void>;
  /** A change is waiting or a save is running. */
  busy: () => boolean;
  /** Forget the waiting changes (the editor is closed); saves already running finish. */
  cancel: () => void;
}

export function createAutosave(save: (key: string) => Promise<void>, opts: { delayMs: number; onIdle?: () => void }): Autosave {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const running = new Map<string, Promise<void>>();
  const busy = () => timers.size > 0 || running.size > 0;
  const settle = () => {
    if (!busy()) opts.onIdle?.();
  };

  const run = (key: string): Promise<void> => {
    clearTimeout(timers.get(key));
    timers.delete(key);
    const next = (running.get(key) ?? Promise.resolve()).then(() => save(key)).catch(() => undefined);
    running.set(key, next);
    void next.finally(() => {
      if (running.get(key) === next) running.delete(key);
      settle();
    });
    return next;
  };

  return {
    schedule: (key) => {
      clearTimeout(timers.get(key));
      timers.set(key, setTimeout(() => void run(key), opts.delayMs));
    },
    flush: async (key) => {
      if (key !== undefined) {
        await run(key);
        return;
      }
      const keys = new Set([...timers.keys(), ...running.keys()]);
      await Promise.all([...keys].map((k) => (timers.has(k) ? run(k) : running.get(k))));
    },
    busy,
    cancel: () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    },
  };
}
